import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { calendarApi, setCalendarToken } from './calendarApi';
import CalendarStatsLine from './CalendarStatsLine';

/**
 * Signup, with the card handled by Square and never by us.
 *
 * THE CARD NUMBER NEVER REACHES THIS COMPONENT, let alone our server. The
 * Square Web Payments SDK renders its own iframe into the container below, and
 * tokenize() hands back a single use nonce. That nonce is the only thing
 * posted, which is what keeps this form out of scope for handling card data.
 *
 * Same SDK, same loading pattern and same tokenize flow as the tenant signup in
 * pages/SignupPayment.js. The difference is which endpoint receives the nonce
 * and which token is stored afterwards: a calendar subscriber is not a tenant
 * admin and their sessions must never be interchangeable.
 *
 * WHAT HAPPENS AFTER 201 IS THE PARENT'S JOB, and it did not used to happen at
 * all. This component stored the token and called back, and the callback only
 * reloaded the session, so the form stayed on screen with the card fields still
 * in it. The first real subscriber was charged $24 and shown a page that looked
 * exactly like the page they had pressed the button on. Moving the whole
 * transition into one callback on the parent, which owns both the router and
 * the banner, is what makes it a single decision instead of three that can each
 * be half done.
 *
 * THE SQUARE LOCATION THE FORM TOKENISES WITH IS NOT THE ONE THAT GETS BILLED.
 * REACT_APP_SQUARE_LOCATION_ID is handed to Square.payments() to build the card
 * iframe; the resulting nonce goes to Cards API createCard, which takes no
 * location at all, and the subscription the server then creates names its own.
 * So the browser's location and the billing location are independent, and this
 * file does not need a second variable to follow the server's move to the Your
 * First Parcel location. Digital wallets would change that, because Apple Pay
 * and Google Pay verify a domain against a specific location, and this form
 * offers neither.
 */

const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
const IS_PRODUCTION = process.env.REACT_APP_SQUARE_ENVIRONMENT === 'production';

const PLANS = [
  {
    key: 'monthly',
    name: 'Monthly',
    price: '$24',
    per: 'per month',
    note: 'Cancel any time, effective at the end of the month you have paid for.'
  },
  {
    key: 'annual',
    name: 'Annual',
    price: '$199',
    per: 'per year',
    note: 'Two months cheaper than paying monthly.'
  }
];

function CalendarSubscribe({ onSubscribed, initialEmail = '' }) {
  const [plan, setPlan] = useState('monthly');
  // Seeded once rather than synced. A cancelled subscriber arriving from
  // /resubscribe gets their own address filled in, and can still change it,
  // which is what signing up on a different address looks like.
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const cardContainerRef = useRef(null);
  const submitLockRef = useRef(false);

  const configured = Boolean(SQUARE_APP_ID && SQUARE_LOCATION_ID);

  useEffect(() => {
    if (!configured || window.Square) {
      if (window.Square) setSquareLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = IS_PRODUCTION
      ? 'https://web.squarecdn.com/v1/square.js'
      : 'https://sandbox.web.squarecdn.com/v1/square.js';
    script.async = true;
    script.onload = () => setSquareLoaded(true);
    script.onerror = () => setError('The payment form could not load. Please refresh and try again.');
    document.body.appendChild(script);
  }, [configured]);

  const initialiseCard = useCallback(async () => {
    if (!squareLoaded || !window.Square || !cardContainerRef.current || card) return;
    try {
      const payments = await window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
      const instance = await payments.card();
      await instance.attach(cardContainerRef.current);
      setCard(instance);
    } catch {
      setError('The payment form could not start. Please refresh and try again.');
    }
  }, [squareLoaded, card]);

  useEffect(() => { initialiseCard(); }, [initialiseCard]);

  const submit = async (event) => {
    event.preventDefault();

    // Two clicks must not become two subscriptions. The server is idempotent
    // as well, but the cheapest place to stop a double charge is before the
    // card is tokenised a second time.
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    setError('');
    setBusy(true);

    try {
      if (!card) {
        setError('The payment form is not ready yet.');
        return;
      }

      const result = await card.tokenize();
      if (result.status !== 'OK') {
        const detail = (result.errors || []).map(e => e.message).join(', ');
        setError(detail || 'Please check your card details and try again.');
        return;
      }

      const data = await calendarApi('/subscribe', {
        method: 'POST',
        body: JSON.stringify({ email, password, plan, cardNonce: result.token })
      });

      // The token first, because everything the parent does next needs a
      // session: it refreshes /me, shows the banner, and sends them to the
      // calendar they have just paid for. A 402 never reaches here; calendarApi
      // throws with Square's own wording about the card, which is caught below
      // and rendered under the form.
      setCalendarToken(data.token);
      await onSubscribed(data.subscriber);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      submitLockRef.current = false;
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '16px', // 16px or larger, or iOS zooms the page on focus.
    borderRadius: '6px',
    border: '1px solid var(--color-border)',
    background: 'var(--color-input-bg, var(--color-surface))',
    color: 'var(--color-text)',
    boxSizing: 'border-box'
  };

  return (
    <div style={{ maxWidth: '460px', margin: '0 auto', padding: '40px 16px 64px' }}>
      <h1 style={{ color: 'var(--color-heading)', fontSize: '1.5rem', marginBottom: '4px' }}>
        Subscribe to the Auction Calendar
      </h1>
      <CalendarStatsLine />

      {!configured && (
        <p style={{ color: 'var(--color-danger-text, var(--color-text))', marginBottom: '20px' }}>
          Subscriptions are not open yet. Please check back shortly.
        </p>
      )}

      <form onSubmit={submit}>
        <fieldset style={{ border: 'none', padding: 0, margin: '0 0 20px' }}>
          <legend style={{ fontWeight: 600, marginBottom: '8px', padding: 0 }}>Choose a plan</legend>
          {PLANS.map(option => (
            <label
              key={option.key}
              style={{
                display: 'block',
                border: `2px solid ${plan === option.key ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderRadius: '8px',
                padding: '12px 14px',
                marginBottom: '8px',
                cursor: 'pointer',
                background: 'var(--color-surface)'
              }}
            >
              <input
                type="radio"
                name="plan"
                value={option.key}
                checked={plan === option.key}
                onChange={() => setPlan(option.key)}
                style={{ marginRight: '10px' }}
              />
              <strong style={{ color: 'var(--color-heading)' }}>{option.name}</strong>
              <span style={{ marginLeft: '8px', color: 'var(--color-text)' }}>
                {option.price} {option.per}
              </span>
              <span style={{
                display: 'block',
                marginTop: '4px',
                marginLeft: '24px',
                color: 'var(--color-text-muted)',
                fontSize: '0.85rem'
              }}>
                {option.note}
              </span>
            </label>
          ))}
        </fieldset>

        <label htmlFor="subscribe-email" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
          Email
        </label>
        <input
          id="subscribe-email"
          type="email"
          autoComplete="username"
          style={{ ...inputStyle, marginBottom: '16px' }}
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
        />

        <label htmlFor="subscribe-password" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
          Choose a password
        </label>
        <input
          id="subscribe-password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          style={{ ...inputStyle, marginBottom: '4px' }}
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
        />
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', margin: '0 0 16px' }}>
          At least 8 characters.
        </p>

        <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Card</label>
        <div
          ref={cardContainerRef}
          style={{
            minHeight: '50px',
            border: '1px solid var(--color-border)',
            borderRadius: '6px',
            padding: '12px',
            background: 'var(--color-input-bg, var(--color-surface))',
            marginBottom: '20px'
          }}
        >
          {!squareLoaded && (
            <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Loading the payment form</p>
          )}
        </div>

        {/*
          Under the form and above the button, which is where somebody looks
          after pressing it. A decline arrives here in Square's own words: "card
          declined", "cvv incorrect", and the server adds that nothing was
          charged. role=alert so a screen reader is told without having to find
          it.
        */}
        {error && (
          <p role="alert" style={{ color: 'var(--color-danger-text, var(--color-text))', marginBottom: '16px' }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          style={{ width: '100%', padding: '12px', fontSize: '1rem' }}
          disabled={busy || !card || !configured}
        >
          {busy ? 'Charging your card' : `Subscribe ${plan === 'annual' ? '$199 per year' : '$24 per month'}`}
        </button>
      </form>

      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem', marginTop: '20px', lineHeight: 1.6 }}>
        You can cancel at any time from your account page. Cancelling takes
        effect at the end of the period you have already paid for, and partial
        periods are not refunded. Dates are provided as researched and must be
        confirmed with the county before you travel or bid. See our{' '}
        <Link to="/terms" style={{ color: 'var(--color-primary)' }}>terms</Link> and{' '}
        <Link to="/privacy" style={{ color: 'var(--color-primary)' }}>privacy policy</Link>.
      </p>

      <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem', marginTop: '20px' }}>
        Already subscribe? <Link to="/login" style={{ color: 'var(--color-primary)' }}>Sign in</Link>
      </p>
    </div>
  );
}

export default CalendarSubscribe;
